import type { WorldDefinition } from "./worldTypes";
import { WORLD_1_LEVELS } from "./world1";
import { WORLD_2_LEVELS } from "./world2";
import { WORLD_3_LEVELS } from "./world3";
import { WORLD_4_LEVELS } from "./world4";
import { WORLD_5_LEVELS } from "./world5";

/**
 * Every world in the campaign, in play order. Adding a world means adding
 * one more entry here (plus its `worldN.ts` level file) - nothing else
 * (Level Select, World Select, `ProgressStorage`) hardcodes a world count
 * or a specific world's identity.
 */
const WORLDS: WorldDefinition[] = [
  {
    id: "w1",
    name: "THE MOON",
    order: 1,
    theme: {
      primary: 0x1c2030,
      secondary: 0x2a2a45,
      // Silver/gray sphere - the moon's own identity - with a deep
      // indigo/violet secondary tint (the card gradient band) for the
      // "gray, dark blue, violet" palette this world is built around.
      accent: 0xc7cede,
      contour: 0x5b6b8c,
      symbol: "moon",
    },
    levelIds: WORLD_1_LEVELS.map((l) => l.id),
    unlock: { type: "always" },
  },
  {
    id: "w2",
    name: "EARTH",
    order: 2,
    theme: {
      primary: 0x123a4a,
      secondary: 0x0f4f4a,
      // Turquoise ocean sphere with green continents.
      accent: 0x4fd8c9,
      contour: 0x3ecf6e,
      symbol: "earth",
    },
    levelIds: WORLD_2_LEVELS.map((l) => l.id),
    unlock: { type: "requires-world-complete", worldId: "w1" },
  },
  {
    id: "w3",
    name: "MARS",
    order: 3,
    theme: {
      primary: 0x3a2418,
      secondary: 0x5c2f1f,
      // Rust/ochre sphere with darker crater and canyon contour detail.
      accent: 0xd97a4a,
      contour: 0x8b4a2b,
      symbol: "mars",
    },
    levelIds: WORLD_3_LEVELS.map((l) => l.id),
    unlock: { type: "requires-world-complete", worldId: "w2" },
  },
  {
    id: "w4",
    name: "SATURN",
    order: 4,
    theme: {
      primary: 0x171a2f,
      secondary: 0x2b3158,
      // Muted gold sphere with pale ring tones - deep navy/restrained
      // orbital feel, distinct from Mars's rust/ochre.
      accent: 0xd6b56d,
      contour: 0x8c7748,
      symbol: "saturn",
    },
    levelIds: WORLD_4_LEVELS.map((l) => l.id),
    unlock: { type: "requires-world-complete", worldId: "w3" },
  },
  {
    id: "w5",
    name: "SUN",
    order: 5,
    theme: {
      // Solar Core visual language - a hot, saturated palette that
      // reads as fundamentally more intense than every prior world:
      // near-black warm charcoal (not Saturn's cool navy), a deep ember
      // red secondary, an incandescent solar orange-gold accent
      // (brighter/more saturated than Saturn's muted gold), and a
      // pale white-hot contour - no prior world uses a near-white
      // contour, which alone reads as "hotter than everything before
      // it".
      primary: 0x1a0f08,
      secondary: 0x5c1710,
      accent: 0xffa62b,
      contour: 0xfff0d0,
      symbol: "sun",
    },
    levelIds: WORLD_5_LEVELS.map((l) => l.id),
    unlock: { type: "requires-world-complete", worldId: "w4" },
  },
];

const BY_ID = new Map<string, WorldDefinition>(WORLDS.map((w) => [w.id, w]));
const SORTED = [...WORLDS].sort((a, b) => a.order - b.order);

export const WorldRegistry = {
  all(): readonly WorldDefinition[] {
    return SORTED;
  },

  byId(id: string): WorldDefinition | undefined {
    return BY_ID.get(id);
  },

  /** The world immediately after `worldId` in campaign order, if any. */
  nextOf(worldId: string): WorldDefinition | undefined {
    const world = BY_ID.get(worldId);
    if (!world) return undefined;
    return SORTED.find((w) => w.order === world.order + 1);
  },

  /** Whether `world` is unlocked, given the set of world ids fully completed so far. */
  isUnlocked(world: WorldDefinition, completedWorldIds: readonly string[]): boolean {
    return world.unlock.type === "always" || completedWorldIds.includes(world.unlock.worldId);
  },
};
